import * as yup from '@if/core/yup'
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material'
import useValueList from '@src/data/api/config-api'
import { EValueList } from '@src/data/types/Constants'
import { autoCompleteOnChangeAdapter } from '@src/services/mui-form-adapter'
import { forwardRef, useMemo } from 'react'
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export const MerchantTagsSchema = yup.object().shape({
  tags: yup.array().of(yup.string().required()).required().default([]),
})

const MerchantTagsInput = forwardRef<ControllerRenderProps, ControllerRenderProps>(({ onChange, ...props }, ref) => {
  const { t } = useTranslation()
  const [tags, isFetchingTags] = useValueList(EValueList.Tags)

  const tagsMap = useMemo(() => {
    const map = new Map<string, string>()
    tags?.forEach((item) => map.set(item.id, item.text))
    return map
  }, [tags])

  return (
    <Autocomplete
      {...props}
      ref={ref}
      onChange={autoCompleteOnChangeAdapter(onChange)}
      multiple
      loading={isFetchingTags}
      options={Array.from(tagsMap.keys())}
      getOptionLabel={(opt: string) => tagsMap.get(opt)!}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('merchant.tags')}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetchingTags ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderValue={(tagValue, getTagsProps) =>
        tagValue.map((option, index) => <Chip {...getTagsProps({ index })} key={option} label={tagsMap.get(option)} />)
      }
    />
  )
})

MerchantTagsInput.displayName = 'MerchantTagsInput'

const MerchantTagsPanel = () => {
  const { t } = useTranslation()
  const { control } = useFormContext<yup.InferSubmit<typeof MerchantTagsSchema>>()

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('merchant.associate_tags')}
      </Typography>
      <Controller
        name="tags"
        control={control}
        render={({ field, fieldState }) => (
          <FormControl error={fieldState.invalid} fullWidth>
            <MerchantTagsInput {...field} />
            <FormHelperText>{fieldState.error?.message}</FormHelperText>
          </FormControl>
        )}
      />
    </Box>
  )
}

export default MerchantTagsPanel
